import type { Brief, BriefContributor, TrackPrompt } from "@/types"

// Curated, art-directed content for The Lists Desk (/design).
// Everything here is hand-written to read like the live product —
// the manual never shows lorem ipsum or "Item 1".

const user = (
  id: number,
  display_name: string,
  tagline: string | null,
): BriefContributor["user"] => ({
  id,
  display_name,
  slug: null,
  has_public_profile: false,
  avatar_url: null,
  tagline,
  pronouns: null,
})

export const FEATURED_CONTRIBUTORS: BriefContributor[] = [
  {
    id: 1,
    role: "lead_curator",
    featured: true,
    display_name: "Priya Raman",
    user: user(
      1,
      "Priya Raman",
      "Songwriter. Believes a chorus can hold you up.",
    ),
  },
  {
    id: 2,
    role: "clinical_advisor",
    featured: true,
    display_name: "June Okafor",
    user: user(
      2,
      "June Okafor",
      "Counsellor. Collects songs her clients bring her.",
    ),
  },
]

export const TRACK_CONTRIBUTORS: BriefContributor[] = [
  {
    id: 3,
    role: "contributor",
    featured: false,
    display_name: "Theo Marsh",
    user: user(3, "Theo Marsh", "Found the right song on the worst night."),
  },
  {
    id: 4,
    role: "contributor",
    featured: false,
    display_name: "Sam Whitlock",
    user: null,
  },
  {
    id: 5,
    role: "contributor",
    featured: false,
    display_name: "Mara Quinn",
    user: user(5, "Mara Quinn", "Shares what got her through the quiet weeks."),
  },
  {
    id: 6,
    role: "contributor",
    featured: false,
    display_name: "Alex Petrov",
    user: null,
  },
]

export const ALL_CONTRIBUTORS = [
  ...FEATURED_CONTRIBUTORS,
  ...TRACK_CONTRIBUTORS,
]

// A complete Brief in the server wire shape, so real briefs components
// (CollabCard, ProgressIndicator, ContributorsSidebar) render unchanged.
export const BRIEF_FIXTURE: Brief = {
  id: 101,
  title: "Songs that got me through the first year",
  slug: "songs-first-year",
  summary:
    "Tracks people reached for after a first diagnosis — and the stories behind them.",
  subtitle:
    "Tracks people reached for after a first diagnosis — and the stories behind them.",
  hero_prompt: null,
  strategic_context: null,
  main_headline: null,
  main_body: null,
  footer_cta_text: null,
  footer_cta_button: null,
  status: "published",
  published_at: "2026-05-04T09:00:00Z",
  cover_image_url: null,
  cover_image_focal_point: null,
  suggestions_count: 23,
  contributors_count: 6,
  progress: {
    filled_slots_count: 7,
    total_slots: 10,
    percent: 70,
    almost_there: true,
    total_suggestions_count: 23,
  },
  track_prompts: [],
  featured_contributors: FEATURED_CONTRIBUTORS,
  track_contributors: TRACK_CONTRIBUTORS,
  brief_faqs: [],
  taggings: [
    {
      id: 1,
      origin: "editorial",
      decision: "kept",
      is_ghost: false,
      is_visible: true,
      tag: {
        id: 1,
        name: "topic:first-steps",
        slug: "first-steps",
        taxonomy: "topic",
        layer: "strategic",
      },
    },
    {
      id: 2,
      origin: "curator",
      decision: "kept",
      is_ghost: false,
      is_visible: true,
      tag: {
        id: 2,
        name: "format:music",
        slug: "music",
        taxonomy: "format",
        layer: "container",
      },
    },
  ],
  editorial_strategy: null,
  viewer_state: "anonymous",
  participation_state: {
    state: "almost_there",
    message: "Three slots left — this one is nearly a playlist.",
    button_label: "Suggest a track",
    button_href: null,
  },
  has_suggestions_from_visitor: false,
  requires_guest_identity: true,
  canonical_url: "https://herdli.st/briefs/songs-first-year",
}

// "Why it matters" notes — the product’s foreground content.
export interface WhyNote {
  id: number
  contributor: string
  track: string
  artist: string
  note: string
}

export const TRACK_PROMPT_FIXTURE: TrackPrompt = {
  id: 1,
  title: "The song that got you through",
  guidance: "Not the one that fixed anything — the one that kept you company.",
  content_type: "audio",
  position: 4,
  suggestions_count: 7,
  slot_state: "pending",
  status: {
    state: "open_for_suggestions",
    suggestion_count: 7,
    accepted_contributor_name: null,
    can_suggest: true,
  },
  track_suggestions: [],
}

export const WHY_NOTES: WhyNote[] = [
  {
    id: 1,
    contributor: "Theo Marsh",
    track: "The Night We Met",
    artist: "Lord Huron",
    note: "I played this on repeat the week after my diagnosis. It didn’t fix anything — it just sat with me, and that was enough.",
  },
  {
    id: 2,
    contributor: "Mara Quinn",
    track: "Breathe Me",
    artist: "Sia",
    note: "My sister sent me this with the message ‘you don’t have to reply’. That permission mattered more than the song.",
  },
  {
    id: 3,
    contributor: "Sam Whitlock",
    track: "Weird Fishes / Arpeggi",
    artist: "Radiohead",
    note: "The build in the second half feels like surfacing. I’d put it on for the walk to every appointment.",
  },
  {
    id: 4,
    contributor: "June Okafor",
    track: "Holocene",
    artist: "Bon Iver",
    note: "A client once told me this song made feeling small feel survivable. I’ve passed that on a dozen times since.",
  },
]

// Suggestion rows for the data-table plate.
export interface SuggestionRow {
  id: number
  track: string
  artist: string
  contributor: string
  note: string
  format: "Music" | "Video" | "Article"
  duration: string
  added: string
}

export const SUGGESTION_ROWS: SuggestionRow[] = [
  {
    id: 1,
    track: "The Night We Met",
    artist: "Lord Huron",
    contributor: "Theo Marsh",
    note: "It didn’t fix anything — it just sat with me.",
    format: "Music",
    duration: "3:28",
    added: "May 6, 2026",
  },
  {
    id: 2,
    track: "Breathe Me",
    artist: "Sia",
    contributor: "Mara Quinn",
    note: "Sent with the message ‘you don’t have to reply’.",
    format: "Music",
    duration: "4:32",
    added: "May 9, 2026",
  },
  {
    id: 3,
    track: "How to be alone (and happy about it)",
    artist: "School of Life",
    contributor: "Sam Whitlock",
    note: "Watched it twice. The second time I took notes.",
    format: "Video",
    duration: "7:41",
    added: "May 14, 2026",
  },
  {
    id: 4,
    track: "Holocene",
    artist: "Bon Iver",
    contributor: "June Okafor",
    note: "Made feeling small feel survivable.",
    format: "Music",
    duration: "5:36",
    added: "May 21, 2026",
  },
  {
    id: 5,
    track: "What grief taught me about joy",
    artist: "The Marginalian",
    contributor: "Alex Petrov",
    note: "The paragraph about ‘parallel truths’ rewired me.",
    format: "Article",
    duration: "11 min",
    added: "Jun 2, 2026",
  },
]
